"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Icon, type IconName } from "@/components/ui/icon";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createJob,
  updateJob,
  deleteStage,
  addStage,
  updateStage,
  reorderStages,
  importAtsJob,
  getJob,
} from "@/lib/api/generated";
import { useCloneJob } from "@/lib/api/generated";
import { useAuth } from "@/features/auth";
import { showToast } from "@/components/ui/sonner";
import {
  isPermissionDenied,
  getPermissionErrorMessage,
} from "@/lib/permissions";
import { stripHtmlTags } from "@/lib/string/strip/strip-html";
import {
  useJobWizardStore,
  type WizardStageItem,
} from "@/features/jobs/store/job-store";
import { StagePresetActions } from "@/features/presets";
import type { StageItem } from "@/features/jobs/components/stage-cards";
import {
  mapApiStages,
  mapJobToWizardForm,
} from "@/features/jobs/store/job-form-mapping";
import useIsMobile from "@/hooks/use-is-mobile";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Modal, ModalContent, ModalTitle } from "@/components/ui/modal";
import {
  STEP_ICONS,
  stepTitles,
  MAX_JOB_DESCRIPTION_LENGTH,
} from "./constants";
import {
  WizardSidebar,
  RadialProgress,
  useStepProgress,
} from "./wizard-sidebar";
import { JobCloneNotice } from "./clone-notice";
import { StepContent } from "./step-content";
import { WizardFooter, type Step3UploadStatus } from "./wizard-footer";

const WIZARD_EXIT_MS = 320;

export function CreateJobWizard() {
  const {
    step,
    maxStepReached,
    form,
    mode,
    cloneSourceId,
    cloneSourceName,
    cloneTargetProjectId,
    closeWizard,
    nextStep,
    setStep,
    updateForm,
    createdJobId,
    setCreatedJobId,
  } = useJobWizardStore();
  const isCloneMode = mode === "clone";
  const queryClient = useQueryClient();
  const cloneJobMutation = useCloneJob();
  const router = useRouter();
  const backendStageIds = useRef<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(true);

  // The selected project is a single user-level value. Resolving it by
  // scanning a page of /api/projects silently yields null once the project
  // falls outside that page (per_page defaults to 20, ordered newest first),
  // which disables the whole step.
  const { selectedProjectId } = useAuth();
  const targetProjectId = isCloneMode
    ? (cloneTargetProjectId ?? selectedProjectId)
    : selectedProjectId;

  const handleApplyPresetStages = useCallback(
    (nextStages: StageItem[]) => {
      updateForm("stages", nextStages as WizardStageItem[]);
    },
    [updateForm],
  );

  const [stepping, setStepping] = useState(false);

  const syncStagesFromApi = useCallback(
    (
      stages: {
        id: string;
        name: string;
        color: string;
        description?: string;
        position: number;
        is_system: boolean;
      }[],
    ) => {
      const mapped = mapApiStages(stages);
      updateForm("stages", mapped);
      backendStageIds.current = new Set(
        [...stages].sort((a, b) => a.position - b.position).map((s) => s.id),
      );
    },
    [updateForm],
  );

  useEffect(() => {
    if (
      createdJobId &&
      form.stages.length > 0 &&
      backendStageIds.current.size === 0
    ) {
      backendStageIds.current = new Set(
        form.stages.filter((s) => !s.id.startsWith("custom-")).map((s) => s.id),
      );
    }
  }, [createdJobId, form.stages]);

  useEffect(() => {
    if (dialogOpen) return;
    const id = window.setTimeout(() => {
      closeWizard();
    }, WIZARD_EXIT_MS);
    return () => window.clearTimeout(id);
  }, [dialogOpen, closeWizard]);

  const draftMutation = useMutation({
    mutationFn: async () => {
      if (createdJobId) {
        return updateJob(createdJobId, {
          title: form.title || "Untitled Job",
          description: form.description || undefined,
          job_manager_id: form.managerId || undefined,
          employer_id: form.employerId || undefined,
        });
      }
      if (!targetProjectId) {
        throw new Error("No project selected");
      }
      const payload = {
        title: form.title || "Untitled Job",
        description: form.description || undefined,
        project_id: targetProjectId,
        job_manager_id: form.managerId!,
        employer_id: form.employerId!,
        status: "draft" as const,
      };
      if (isCloneMode && cloneSourceId) {
        return cloneJobMutation.mutateAsync({
          jobId: cloneSourceId,
          data: payload,
        });
      }
      return createJob(payload);
    },
    onSuccess: (job) => {
      setCreatedJobId(job.id, job.project_id ?? targetProjectId ?? null);
      if (job.stages?.length) {
        syncStagesFromApi(job.stages);
      }
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      setStepping(true);
      setTimeout(() => {
        nextStep();
        setStepping(false);
      }, 400);
    },
    onError: (err: Error) => {
      if (isPermissionDenied(err)) {
        showToast.error("Permission denied", {
          description: getPermissionErrorMessage(err),
        });
      } else {
        showToast.error(
          isCloneMode ? "Failed to clone job" : "Failed to save job",
          { description: err.message },
        );
      }
    },
  });

  // "Import with an ATS": the backend import endpoint creates the active Arbi
  // job (title/description/stages) from the remote job AND kicks off a
  // background candidate pull. We then load that job into the wizard and walk
  // the user through the remaining (read-only) steps instead of navigating away.
  const importMutation = useMutation({
    mutationFn: async () => {
      if (!targetProjectId) {
        throw new Error("No project selected");
      }
      const link = await importAtsJob(crypto.randomUUID(), {
        connection_id: form.connectionId!,
        remote_job_id: form.remoteJobId!,
        project_id: targetProjectId,
        employer_id: form.employerId!,
        job_manager_id: form.managerId || null,
      });
      // Best-effort: load the created job so steps 2-3 preview real data. If it
      // fails, the import still succeeded — fall back to id-only.
      let job: Awaited<ReturnType<typeof getJob>> | null = null;
      try {
        job = await getJob(link.job_id);
      } catch {
        job = null;
      }
      return { link, job };
    },
    onSuccess: ({ link, job }) => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      showToast.success("Imported from ATS", {
        description: "The job and its candidates are syncing in.",
      });
      if (!job) {
        // getJob failed — we can't populate the walkthrough with the real
        // stages/description, so don't walk it over placeholder state; fall
        // back to the original behavior and go straight to the created job.
        setCreatedJobId(link.job_id, targetProjectId);
        closeWizard();
        router.push(`/jobs/${link.job_id}`);
        return;
      }
      const patch = mapJobToWizardForm(job);
      updateForm("title", patch.title);
      updateForm("description", patch.description);
      updateForm("stages", mapApiStages(job.stages ?? []));
      setCreatedJobId(link.job_id, job.project_id ?? targetProjectId);
      setStepping(true);
      setTimeout(() => {
        nextStep();
        setStepping(false);
      }, 400);
    },
    onError: (err: Error) => {
      if (isPermissionDenied(err)) {
        showToast.error("Permission denied", {
          description: getPermissionErrorMessage(err),
        });
      } else {
        showToast.error("Couldn't import from ATS", {
          description: err.message,
        });
      }
    },
  });

  const syncStagesMutation = useMutation({
    mutationFn: async () => {
      if (!createdJobId) throw new Error("No job ID");

      const currentStages = form.stages;
      const currentIds = new Set(currentStages.map((s) => s.id));
      const knownBackendIds = backendStageIds.current;

      const deletedIds = [...knownBackendIds].filter(
        (id) =>
          !currentIds.has(id) &&
          !currentStages.some((s) => s.id === id && s.is_system),
      );
      for (const id of deletedIds) {
        await deleteStage(createdJobId, id);
      }

      const resolvedIds: string[] = [];

      for (const stage of currentStages) {
        if (stage.is_system) continue;

        if (stage.id.startsWith("custom-")) {
          const created = await addStage(createdJobId, {
            name: stage.name,
            color: stage.color,
            description: stage.description,
          });
          resolvedIds.push(created.id);
        } else if (knownBackendIds.has(stage.id)) {
          await updateStage(createdJobId, stage.id, {
            name: stage.name,
            color: stage.color,
            description: stage.description,
          });
          resolvedIds.push(stage.id);
        }
      }

      if (resolvedIds.length > 0) {
        await reorderStages(createdJobId, { stage_ids: resolvedIds });
      }

      return updateJob(createdJobId, { wizard_step: 2 });
    },
    onSuccess: (job) => {
      if (job?.stages?.length) {
        syncStagesFromApi(job.stages);
      }
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      setStepping(true);
      setTimeout(() => {
        nextStep();
        setStepping(false);
      }, 400);
    },
    onError: (err: Error) => {
      showToast.error("Failed to save stages", { description: err.message });
    },
  });

  const advanceMutation = useMutation({
    mutationFn: async (wizardStep: number) => {
      if (!createdJobId) return;
      if (wizardStep === 3) {
        await updateJob(createdJobId, {
          status: "active",
          wizard_step: wizardStep,
        });
      } else {
        await updateJob(createdJobId, { wizard_step: wizardStep });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      setStepping(true);
      setTimeout(() => {
        nextStep();
        setStepping(false);
      }, 400);
    },
    onError: (err: Error) => {
      showToast.error("Failed to save progress", { description: err.message });
    },
  });

  const descriptionText = stripHtmlTags(form.description);
  const step1Valid =
    form.sourcingType === "ats_import"
      ? // Import path: connection + remote job + employer (title/description come
        // from the remote job). Manager is optional for import.
        !!form.connectionId && !!form.remoteJobId && !!form.employerId
      : form.title.trim().length >= 3 &&
        !!form.managerId &&
        !!form.employerId &&
        !!targetProjectId &&
        descriptionText.length > 0 &&
        form.description.length <= MAX_JOB_DESCRIPTION_LENGTH;

  const isBusy =
    draftMutation.isPending ||
    cloneJobMutation.isPending ||
    importMutation.isPending ||
    syncStagesMutation.isPending ||
    advanceMutation.isPending ||
    stepping;
  const isMobile = useIsMobile();
  const stepProgress = useStepProgress(step);
  const titles = stepTitles(form.sourcingType);
  const importLocked = form.sourcingType === "ats_import" && !!createdJobId;

  // Upload status drives the step 3 CTA button: "Uploading" (disabled),
  // "N Issues Found" (disabled/warning), or "Create Job" (blue primary).
  const uploads = form.uploadedFiles;
  const uploadingCount = uploads.filter((f) => f.state === "uploading").length;
  const failedCount = uploads.filter((f) => f.state === "failed").length;
  const step3UploadStatus: Step3UploadStatus =
    uploadingCount > 0 ? "uploading" : failedCount > 0 ? "failed" : "idle";

  const handleNext = useCallback(() => {
    if (step === 1) {
      if (!step1Valid) return;
      if (form.sourcingType === "ats_import") {
        importMutation.mutate();
        return;
      }
      draftMutation.mutate();
      return;
    }
    if (step === 2) {
      // Both paths persist here: for ats_import the stages already exist
      // server-side, and the list only permits update/reorder (no add/delete),
      // so the sync reduces to updateStage + reorderStages.
      syncStagesMutation.mutate();
      return;
    }
    if (step === 3) {
      if (form.sourcingType === "ats_import") {
        setStepping(true);
        setTimeout(() => {
          nextStep();
          setStepping(false);
        }, 200);
        return;
      }
      advanceMutation.mutate(3);
      return;
    }
  }, [
    step,
    step1Valid,
    form.sourcingType,
    draftMutation,
    importMutation,
    syncStagesMutation,
    advanceMutation,
    nextStep,
  ]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && step < 4) setDialogOpen(false);
    },
    [step],
  );

  const isNextDisabled =
    isBusy ||
    (step === 1 && !step1Valid) ||
    (step === 3 && step3UploadStatus !== "idle");

  return (
    <Modal
      open={dialogOpen}
      onOpenChange={handleOpenChange}
      dismissible={step < 4}
    >
      <ModalContent
        size="xl"
        className={
          isMobile
            ? "max-h-[95dvh]"
            : `max-w-[880px] ${isCloneMode ? "h-[775px]" : "h-[700px]"} flex-row p-0`
        }
      >
        <ModalTitle className="sr-only">Job Setup</ModalTitle>

        {isMobile ? (
          <>
            <div className="pt-4 flex flex-col gap-3 shrink-0">
              <div className="px-4 flex items-center justify-between">
                <div className="flex gap-1.5 items-center">
                  <Icon
                    name={(STEP_ICONS[step] || "IconEditBig") as IconName}
                    size={16}
                    className="text-heading opacity-60"
                  />
                  <span className="text-sm font-semibold text-heading">
                    {titles[step]}
                  </span>
                </div>
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map((s) => (
                    <RadialProgress
                      key={s}
                      stepNum={s}
                      isActive={s === step && step !== 4}
                      isComplete={s < step || (s === 4 && step === 4)}
                      progress={stepProgress[s] ?? 0}
                    />
                  ))}
                </div>
              </div>
              {step === 2 && form.sourcingType !== "ats_import" && (
                <div className="px-4 flex justify-end">
                  <StagePresetActions
                    stages={form.stages}
                    onApplyStages={handleApplyPresetStages}
                  />
                </div>
              )}
              {isCloneMode && (
                <div className="px-4">
                  <JobCloneNotice sourceName={cloneSourceName} />
                </div>
              )}
              <div className="h-px bg-contrast/10" />
            </div>

            <ScrollArea className="flex-1 pt-4 overflow-auto min-h-0">
              <div className="px-4 pb-4">
                <StepContent />
              </div>
            </ScrollArea>

            <div className="flex items-center justify-between shrink-0 border-t border-border px-4 py-3">
              <WizardFooter
                onNext={handleNext}
                isBusy={isBusy}
                isNextDisabled={isNextDisabled}
                step3UploadStatus={step3UploadStatus}
                step3FailedCount={failedCount}
              />
            </div>
          </>
        ) : (
          <>
            <div className="w-[301px] shrink-0 bg-surface-header">
              <WizardSidebar
                step={step}
                maxStepReached={maxStepReached}
                onStepClick={(s) => setStep(s as 1 | 2 | 3 | 4)}
                lockFirstStep={importLocked}
              />
            </div>

            <div className="flex-1 bg-surface-elevated flex flex-col h-full w-[579px]">
              <div className="flex flex-col gap-4 flex-1 min-h-0 p-6 pb-0">
                <div className="flex items-center justify-between gap-3 shrink-0">
                  <div className="flex gap-1.5 items-center justify-center min-w-0">
                    <Icon
                      name={(STEP_ICONS[step] || "IconEditBig") as IconName}
                      size={18}
                      className="text-heading opacity-60"
                    />
                    <span className="text-base font-semibold text-heading">
                      {titles[step]}
                    </span>
                  </div>
                  {step === 2 && form.sourcingType !== "ats_import" && (
                    <StagePresetActions
                      stages={form.stages}
                      onApplyStages={handleApplyPresetStages}
                      className="flex items-center gap-2 shrink-0"
                    />
                  )}
                </div>

                <div className="h-px bg-contrast/10 shrink-0" />

                <div className="flex-1 min-h-0 overflow-hidden">
                  <StepContent />
                </div>
              </div>

              <div className="flex items-center justify-between shrink-0 p-6 pt-4">
                <WizardFooter
                  onNext={handleNext}
                  isBusy={isBusy}
                  isNextDisabled={isNextDisabled}
                  step3UploadStatus={step3UploadStatus}
                  step3FailedCount={failedCount}
                />
              </div>
            </div>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

export default CreateJobWizard;
